import { fabric } from 'fabric';

class AlignGuidelines {
  aligningLineMargin = 5;
  aligningLineWidth = 1;
  aligningLineColor = 'rgba(95,166,160,.6)';
  canvas;

  ctx;
  viewportTransform;
  verticalLines = [];
  horizontalLines = [];
  activeObj = new fabric.Object();

  constructor({ canvas, aligningOptions }) {
    this.canvas = canvas;
    this.ctx = canvas.getSelectionContext();

    if (aligningOptions) {
      this.aligningLineMargin = aligningOptions.lineMargin || this.aligningLineMargin;
      this.aligningLineWidth = aligningOptions.lineWidth || this.aligningLineWidth;
      this.aligningLineColor = aligningOptions.lineColor || this.aligningLineColor;
    }
  }

  drawSign(x, y) {
    const ctx = this.ctx;

    ctx.lineWidth = 0.5;
    ctx.strokeStyle = this.aligningLineColor;
    ctx.beginPath();

    const size = 2;
    ctx.moveTo(x - size, y - size);
    ctx.lineTo(x + size, y + size);
    ctx.moveTo(x + size, y - size);
    ctx.lineTo(x - size, y + size);
    ctx.stroke();
  }

  drawLine(x1, y1, x2, y2) {
    const ctx = this.ctx;
    const point1 = fabric.util.transformPoint(
      new fabric.Point(x1, y1),
      this.canvas.viewportTransform,
    );
    const point2 = fabric.util.transformPoint(
      new fabric.Point(x2, y2),
      this.canvas.viewportTransform,
    );

    ctx.save();
    ctx.lineWidth = this.aligningLineWidth;
    ctx.strokeStyle = this.aligningLineColor;
    ctx.beginPath();

    ctx.moveTo(point1.x, point1.y);
    ctx.lineTo(point2.x, point2.y);

    ctx.stroke();

    this.drawSign(point1.x, point1.y);
    this.drawSign(point2.x, point2.y);

    ctx.restore();
  }

  drawVerticalLine(coords, movingCoords) {
    // `movingCoords` is precomputed once in `onAfterRender` and reused for all lines.
    // This avoids recalculating transformed drag coordinates for every single guideline draw.
    if (!movingCoords) {
      return;
    }
    const movingCoordKeys = Object.keys(movingCoords);
    if (!movingCoordKeys.some((key) => Math.abs(movingCoords[key].x - coords.x) < 0.0001)) return;
    this.drawLine(
      coords.x,
      Math.min(coords.y1, coords.y2),
      coords.x,
      Math.max(coords.y1, coords.y2),
    );
  }

  drawHorizontalLine(coords, movingCoords) {
    // Same optimization as vertical lines: receive precomputed moving coords to
    // keep the render phase allocation-free as much as possible.
    if (!movingCoords) {
      return;
    }
    const movingCoordKeys = Object.keys(movingCoords);
    if (!movingCoordKeys.some((key) => Math.abs(movingCoords[key].y - coords.y) < 0.0001)) return;
    this.drawLine(
      Math.min(coords.x1, coords.x2),
      coords.y,
      Math.max(coords.x1, coords.x2),
      coords.y,
    );
  }

  isInRange(value1, value2) {
    return (
      Math.abs(Math.round(value1) - Math.round(value2)) <=
      this.aligningLineMargin / this.canvas.getZoom()
    );
  }

  clearGuideline() {
    this.canvas.clearContext(this.ctx);
  }

  clearLinesMeta() {
    this.verticalLines.length = 0;
    this.horizontalLines.length = 0;
  }

  onMouseDown() {
    this.clearLinesMeta();
    this.viewportTransform = this.canvas.viewportTransform;
  }

  watchMouseDown() {
    this.onMouseDown = this.onMouseDown.bind(this);
    this.canvas.on('mouse:down', this.onMouseDown);
  }

  onMouseUp() {
    this.clearLinesMeta();
    this.canvas.requestRenderAll();
  }

  watchMouseUp() {
    this.onMouseUp = this.onMouseUp.bind(this);
    this.canvas.on('mouse:up', this.onMouseUp);
  }

  onMouseWheel() {
    this.clearLinesMeta();
    this.canvas.requestRenderAll();
  }

  watchMouseWheel() {
    this.onMouseWheel = this.onMouseWheel.bind(this);
    this.canvas.on('mouse:wheel', this.onMouseWheel);
  }

  onObjectMoving(e) {
    this.clearLinesMeta();
    const activeObject = e.target;
    this.activeObj = activeObject;
    const canvasObjects = this.canvas.getObjects();

    const transform = this.canvas._currentTransform;
    if (!transform) return;
    this.traversAllObjects(activeObject, canvasObjects);
  }

  watchObjectMoving() {
    this.onObjectMoving = this.onObjectMoving.bind(this);
    this.canvas.on('object:moving', this.onObjectMoving);
  }

  /**
   *  Once object stops moving, clear the lines and redraw the canvas.
   *  This is to ensure that the guidelines are not left on the canvas after the object is moved.
   */
  onObjectModified() {
    this.clearLinesMeta();
    this.clearGuideline();
    this.canvas.requestRenderAll();
  }

  watchObjectModified() {
    this.onObjectModified = this.onObjectModified.bind(this);
    this.canvas.on('object:modified', this.onObjectModified);
  }

  getObjDraggingObjCoords(activeObject) {
    const aCoords = activeObject.aCoords;
    const center = activeObject.getCenterPoint();
    const centerPoint = new fabric.Point(
      (aCoords.tl.x + aCoords.br.x) / 2,
      (aCoords.tl.y + aCoords.br.y) / 2,
    );

    const offsetX = centerPoint.x - center.x;
    const offsetY = centerPoint.y - center.y;

    // Use a single mutable result object instead of reduce + object spread.
    // In drag events this method is called frequently, so avoiding per-iteration
    // object cloning reduces GC pressure and CPU spikes.
    const result = { c: center };
    const aCoordKeys = Object.keys(aCoords);
    for (let i = 0; i < aCoordKeys.length; i += 1) {
      const key = aCoordKeys[i];
      result[key] = {
        x: aCoords[key].x - offsetX,
        y: aCoords[key].y - offsetY,
      };
    }
    return result;
  }

  omitCoords(objCoords, type) {
    let newCoords;
    if (type === 'vertical') {
      let l = ['tl', objCoords.tl];
      let r = ['tl', objCoords.tl];
      Object.keys(objCoords).forEach((key) => {
        if (objCoords[key].x < l[1].x) {
          l = [key, objCoords[key]];
        }
        if (objCoords[key].x > r[1].x) {
          r = [key, objCoords[key]];
        }
      });
      newCoords = {
        [l[0]]: l[1],
        [r[0]]: r[1],
        c: objCoords.c,
      };
    } else {
      let t = ['tl', objCoords.tl];
      let b = ['tl', objCoords.tl];
      Object.keys(objCoords).forEach((key) => {
        if (objCoords[key].y < t[1].y) {
          t = [key, objCoords[key]];
        }
        if (objCoords[key].y > b[1].y) {
          b = [key, objCoords[key]];
        }
      });
      newCoords = {
        [t[0]]: t[1],
        [b[0]]: b[1],
        c: objCoords.c,
      };
    }
    return newCoords;
  }

  getObjMaxWidthHeightByCoords(coords) {
    const objHeight =
      Math.max(Math.abs(coords.c.y - coords['tl'].y), Math.abs(coords.c.y - coords['tr'].y)) * 2;
    const objWidth =
      Math.max(Math.abs(coords.c.x - coords['tl'].x), Math.abs(coords.c.x - coords['tr'].x)) * 2;
    return { objHeight, objWidth };
  }

  calcCenterPointByACoords(coords) {
    return new fabric.Point((coords.tl.x + coords.br.x) / 2, (coords.tl.y + coords.br.y) / 2);
  }

  traversAllObjects(activeObject, canvasObjects) {
    const objCoordsByMovingDistance = this.getObjDraggingObjCoords(activeObject);
    // Cache keys once per move event to avoid repeated Object.keys inside nested loops.
    const movingCoordKeys = Object.keys(objCoordsByMovingDistance);

    const snapXPoints = [];
    const snapYPoints = [];

    const activeObjectCoordsForLine = activeObject.aCoords
      ? {
          // Precompute center-based coords once and reuse while building guideline line endpoints.
          ...activeObject.aCoords,
          c: this.calcCenterPointByACoords(activeObject.aCoords),
        }
      : null;

    for (let i = canvasObjects.length; i--; ) {
      if (canvasObjects[i] === activeObject) continue;
      const objCoords = {
        ...canvasObjects[i].aCoords,
        c: canvasObjects[i].getCenterPoint(),
      };
      const { objHeight, objWidth } = this.getObjMaxWidthHeightByCoords(objCoords);
      // Compute filtered coord sets once per compared object (instead of once per point pair).
      const horizontalCoords =
        canvasObjects[i].angle !== 0 ? this.omitCoords(objCoords, 'horizontal') : objCoords;
      const verticalCoords =
        canvasObjects[i].angle !== 0 ? this.omitCoords(objCoords, 'vertical') : objCoords;
      const horizontalCoordKeys = Object.keys(horizontalCoords);
      const verticalCoordKeys = Object.keys(verticalCoords);

      for (let m = 0; m < movingCoordKeys.length; m += 1) {
        const activeObjPoint = movingCoordKeys[m];
        const activeCoord = objCoordsByMovingDistance[activeObjPoint];

        for (let h = 0; h < horizontalCoordKeys.length; h += 1) {
          const objPoint = horizontalCoordKeys[h];
          if (!this.isInRange(activeCoord.y, objCoords[objPoint].y)) continue;

          const y = objCoords[objPoint].y;
          const x1 =
            objPoint === 'c'
              ? Math.min(objCoords.c.x - objWidth / 2, activeCoord.x)
              : Math.min(objCoords[objPoint].x, activeCoord.x);
          const x2 =
            objPoint === 'c'
              ? Math.max(objCoords.c.x + objWidth / 2, activeCoord.x)
              : Math.max(objCoords[objPoint].x, activeCoord.x);

          const offset = activeCoord.y - y;
          snapYPoints.push(objCoordsByMovingDistance.c.y - offset);

          if (activeObjectCoordsForLine) {
            const activeLineCoord = activeObjectCoordsForLine[activeObjPoint];
            const lineX1 =
              objPoint === 'c'
                ? Math.min(objCoords.c.x - objWidth / 2, activeLineCoord.x)
                : Math.min(objCoords[objPoint].x, activeLineCoord.x);
            const lineX2 =
              objPoint === 'c'
                ? Math.max(objCoords.c.x + objWidth / 2, activeLineCoord.x)
                : Math.max(objCoords[objPoint].x, activeLineCoord.x);
            this.horizontalLines.push({ y, x1: lineX1, x2: lineX2 });
          } else {
            this.horizontalLines.push({ y, x1, x2 });
          }
        }

        for (let v = 0; v < verticalCoordKeys.length; v += 1) {
          const objPoint = verticalCoordKeys[v];
          if (!this.isInRange(activeCoord.x, objCoords[objPoint].x)) continue;

          const x = objCoords[objPoint].x;
          const y1 =
            objPoint === 'c'
              ? Math.min(verticalCoords.c.y - objHeight / 2, activeCoord.y)
              : Math.min(objCoords[objPoint].y, activeCoord.y);
          const y2 =
            objPoint === 'c'
              ? Math.max(verticalCoords.c.y + objHeight / 2, activeCoord.y)
              : Math.max(objCoords[objPoint].y, activeCoord.y);

          const offset = activeCoord.x - x;
          snapXPoints.push(objCoordsByMovingDistance.c.x - offset);

          if (activeObjectCoordsForLine) {
            const activeLineCoord = activeObjectCoordsForLine[activeObjPoint];
            const lineY1 =
              objPoint === 'c'
                ? Math.min(verticalCoords.c.y - objHeight / 2, activeLineCoord.y)
                : Math.min(objCoords[objPoint].y, activeLineCoord.y);
            const lineY2 =
              objPoint === 'c'
                ? Math.max(verticalCoords.c.y + objHeight / 2, activeLineCoord.y)
                : Math.max(objCoords[objPoint].y, activeLineCoord.y);
            this.verticalLines.push({ x, y1: lineY1, y2: lineY2 });
          } else {
            this.verticalLines.push({ x, y1, y2 });
          }
        }
      }

      this.snap({
        activeObject,
        draggingObjCoords: objCoordsByMovingDistance,
        snapXPoints,
        snapYPoints,
      });
    }
  }

  snap({ activeObject, snapXPoints, draggingObjCoords, snapYPoints }) {
    const nearestPoint = (list, originPoint) => {
      if (!list.length) return originPoint;
      // Linear scan keeps behavior identical (nearest by absolute distance)
      // but avoids map/sort allocations and O(n log n) work on every move tick.
      let nearest = list[0];
      let minDistance = Math.abs(originPoint - nearest);
      for (let i = 1; i < list.length; i += 1) {
        const currentDistance = Math.abs(originPoint - list[i]);
        if (currentDistance < minDistance) {
          minDistance = currentDistance;
          nearest = list[i];
        }
      }
      return nearest;
    };
    activeObject.setPositionByOrigin(
      // auto snap nearest object, record all the snap points, and then find the nearest one
      new fabric.Point(
        nearestPoint(snapXPoints, draggingObjCoords.c.x),
        nearestPoint(snapYPoints, draggingObjCoords.c.y),
      ),
      'center',
      'center',
    );
  }

  onBeforeRender() {
    this.clearGuideline();
  }

  onAfterRender() {
    // Reuse one coordinate snapshot for all draw operations in this frame.
    // This keeps render work stable even when many guides are visible.
    const movingCoords = this.activeObj?.aCoords
      ? this.getObjDraggingObjCoords(this.activeObj)
      : null;
    for (let i = this.verticalLines.length; i--; ) {
      this.drawVerticalLine(this.verticalLines[i], movingCoords);
    }
    for (let i = this.horizontalLines.length; i--; ) {
      this.drawHorizontalLine(this.horizontalLines[i], movingCoords);
    }
    this.canvas.calcOffset();
  }

  watchRender() {
    this.onBeforeRender = this.onBeforeRender.bind(this);
    this.onAfterRender = this.onAfterRender.bind(this);
    this.canvas.on('before:render', this.onBeforeRender);
    this.canvas.on('after:render', this.onAfterRender);
  }

  init() {
    this.watchObjectMoving();
    this.watchRender();
    this.watchMouseDown();
    this.watchMouseUp();
    this.watchMouseWheel();
    this.watchObjectModified();
  }

  reset() {
    this.canvas.off('before:render', this.onBeforeRender);
    this.canvas.off('after:render', this.onAfterRender);
    this.canvas.off('object:moving', this.onObjectMoving);
    this.canvas.off('mouse:down', this.onMouseDown);
    this.canvas.off('mouse:up', this.onMouseUp);
    this.canvas.off('mouse:wheel', this.onMouseWheel);
    this.clearLinesMeta();
    this.clearGuideline();
    this.canvas.requestRenderAll();
  }
}

let alignGuidelines;
let isAlignmentEnabled = true; // Default to enabled

export function handleAlignmentGuides(canvas, enabled = true) {
  if (alignGuidelines) {
    alignGuidelines.reset();
  }

  if (enabled) {
    alignGuidelines = new AlignGuidelines({
      canvas: canvas,
      aligningOptions: {
        lineColor: 'rgba(95,166,160,.6)',
        lineWidth: 1,
        lineMargin: 5,
      },
    });
    alignGuidelines.init();
  }

  isAlignmentEnabled = enabled;
}

export function cancelAlignmentGuides(canvas) {
  handleAlignmentGuides(canvas, false);
  return isAlignmentEnabled;
}

export function isAlignmentGuidesEnabled() {
  return isAlignmentEnabled;
}
